import { Activity, SiteActivity, SiteTopology, ChartSeriesElement } from "../../../domain/cloud-topology";
import moment from 'moment-timezone';
import { ChartService } from "../../../services/chart.service";
import * as HighchartsGantt from 'highcharts/highcharts-gantt';


export class ActivityChart {
  private chartObject: HighchartsGantt.Chart;
  private categories: string[] = [];
  constructor(chartId: string, startDate: string, stopDate: string, private sites: SiteTopology[], private siteSchedules) {
    const series = this.getSeries();
    this.chartObject = HighchartsGantt.chart(chartId, {
      chart: {
        height: series.length * 40 + 300,
        backgroundColor: 'rgba(0,0,0,0)',
        zooming: {
          type: 'x'
        }
      },
      credits: {
        enabled: false
      },
      title: {
        text: 'Site Schedules'
      },
      time: {
        timezone: ChartService.timezone
      },
      series: series,
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          hour: '%l %p',
          day: '%A, %e. %B',
          week: 'Week %W',
          month: '%B'
        },
        min: +moment(startDate).utc(true).startOf('day').format('x'),
        max: +moment(stopDate).utc(true).endOf('day').format('x'),
        tickInterval: 60000
      },
      yAxis: {
        title: { text: '' },
        categories: this.categories, // Set correct categories
        tickWidth: 0,
        tickLength: 0,
        labels: {
          style: { fontSize: '12px' } // Ensure labels are visible
        }
      },
      tooltip: {
        style: {
          pointerEvents: 'auto'
        },
        headerFormat: '{series.name}',
        hideDelay: 1000,
        useHTML: true,
        pointFormat:
          '<br /><span>Type: {point.name}</span>' +
          '<br/>' +
          '<span>From: {point.start: %A, %b %e, %l:%M %p} (' + ChartService.timezone + ')</span>' +
          '<br/>' +
          '<span>To: {point.end: %A, %b %e, %l:%M %p} (' + ChartService.timezone + ')</span>' +
          '<br/><a href="{point.custom.diagnosticsLink}" target="_blank">View Diagnostics</a>' +
          '<br/><a href="{point.custom.siteLink}" target="_blank">View Site Configuration</a>'
      },
      plotOptions: {
        series: {
          pointPlacement: 'on',
        }
      },
      legend: { enabled: false }
    })
  }


  redraw() {
    this.chartObject.redraw();
  }
  resize() {
    this.chartObject.reflow();
  }

  private getSeries(): HighchartsGantt.SeriesOptionsType[] {
    let series: HighchartsGantt.SeriesGanttOptions[] = [];
    this.categories = []; // Reset categories

    for (const siteSchedule of this.siteSchedules.siteActivities) {
      const siteName = this.findSiteName(siteSchedule);

      // Ensure unique categories (site names)
      if (!this.categories.includes(siteName)) {
        this.categories.push(siteName);
      }
    }

    for (const siteSchedule of this.siteSchedules.siteActivities) {
      const siteName = this.findSiteName(siteSchedule);
      const yIndex = this.categories.indexOf(siteName); // Assign correct y-axis index

      let newSeriesElement: HighchartsGantt.SeriesGanttOptions = {
        name: siteName,
        type: 'gantt',
        data: []
      };

      const activities = this.scrubDuplicates(siteSchedule.activities);

      for (const activity of activities) {
        if (activity.enabled) {
          newSeriesElement.data.push({
            start: +moment(activity.startTimeUtc).utc(true).valueOf(),
            end: +moment(activity.endTimeUtc).utc(true).valueOf(),
            name: activity.type,
            color: this.getActivityColor(activity),
            y: this.determineOverlap(activities, activity, yIndex), // Updated to use overlap
            custom: { diagnosticsLink: this.getDiagnosticsLink(siteSchedule), siteLink: this.getSiteLink(siteSchedule) }  // Add diagnostics link
          });
        }
      }

      if (newSeriesElement.data.length > 0) {
        series.push(newSeriesElement);
      }
    }

    return series;
  }

  private scrubDuplicates(activities: Activity[]): Activity[] {
    let scrubbedArray: Activity[] = [];
    for (const activity of activities) {
      const scrubbedActivity = scrubbedArray.find((activityToFind) => {
        return activityToFind.startTimeUtc == activity.startTimeUtc &&
          activityToFind.endTimeUtc == activity.endTimeUtc &&
          activityToFind.type == activity.type;
      });

      if (!scrubbedActivity) {
        scrubbedArray.push(activity);
      }
    }

    return scrubbedArray;
  }

  private determineOverlap(activities: Activity[], activity: Activity, yVal: number): number {
    if (activities[0].id === activity.id) {
      return yVal;
    }

    if (activities[activities.length - 1].id === activity.id) {
      return yVal;
    }

    let idx = activities.findIndex((activityToFind) => {
      return activityToFind.id == activity.id;
    });


    const activityEndDate = +moment(activity.endTimeUtc).utc(true).format('x');
    const nextActivityStart = +moment(activities[idx + 1].startTimeUtc).utc(true).format('x');
    if (nextActivityStart == activityEndDate) {
      return yVal;
    }

    if (nextActivityStart < activityEndDate) {
      return yVal + 0.3;
    } else {
      return yVal;
    }
  }

  private getDiagnosticsLink(siteActivity: SiteActivity): string {
    const host = location.host.replace('control', 'diagnostics');
    const route = '/#/app/home/sites/'

    return `${location.protocol}//${host}${route}${siteActivity.siteId}`;
  }

  private getSiteLink(siteActivity: SiteActivity): string {
    const host = location.host;
    const route = '/#/site/';

    return `${location.protocol}//${host}${route}${siteActivity.siteId}/home`;
  }

  private getActivityColor(activity: Activity) {
    if (activity.type == 'BULK_CHARGE') {
      return "#EECC16";
    }

    if (activity.type == 'BULK_GENERATE') {
      return "#FD817D";
    }

    if (activity.type == 'DEMAND_SETPOINT') {
      return "#4178BC";
    }

    if (activity.type == 'AUTO_OPTIMIZATION') {
      return "#AA71FF";
    }

    if (activity.type == 'DEMAND_RESPONSE') {
      return "#E8384F";
    }
  }

  private findSiteName(siteSchedule: SiteActivity): string {
    return this.sites.find((site) => {
      return site.id == siteSchedule.siteId;
    }).name;
  }
}

